import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axiosInstance from "../../../../Config/axiosInstance";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./ProjectCodeDialog.css";

const animatedComponents = makeAnimated();

const PREVIEW_URL = "/code_registries/preview/";

function getPk(x) {
  if (x == null) return null;

  if (typeof x === "number") return String(x);

  if (typeof x === "string") {
    const s = x.trim();
    return s ? s : null;
  }

  if (typeof x === "object") {
    const maybe =
      x.id ??
      x.value ??
      x.pk ??
      x.organization_id ??
      x.client_id ??
      x.matrix_id ??
      x.opportunities ??
      x.opportunities_id ??
      x.opportunity_id ??
      null;

    return getPk(maybe);
  }

  return null;
}

export default function ProjectCodeEditDialog({ open, onClose, onUpdated, record }) {
  const [organizations, setOrganizations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);

  const [loadingLists, setLoadingLists] = useState(false);
  const [fullRecord, setFullRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);

  const [organization, setOrganization] = useState(null);
  const [department, setDepartment] = useState(null);
  const [activity, setActivity] = useState(null);
  const [client, setClient] = useState(null);

  const [selectedOpportunities, setSelectedOpportunities] = useState([]);
  const [description, setDescription] = useState("");
  const [remarks, setRemarks] = useState("");
  const [nr, setNr] = useState("");
  const [code, setCode] = useState("");

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [errors, setErrors] = useState({
    organization: "",
    department: "",
    activity: "",
    client: "",
    description: "",
    opportunities: "",
    code: "",
    submit: "",
  });

  const initialOrganizationPkRef = useRef(null);
  const initialMatrixPkRef = useRef(null);
  const initialClientPkRef = useRef(null);

  const setSubmitError = (e) => {
    const msg =
      typeof e?.response?.data === "string"
        ? `${e.response.status} ${e.response.statusText}`
        : e?.response?.data
          ? JSON.stringify(e.response.data)
          : e?.message || "Unknown error";

    setErrors((prev) => ({ ...prev, submit: msg }));
  };

  useEffect(() => {
    if (!open) return;

    setFullRecord(null);

    if (!record?.id) {
      setFullRecord(record || null);
      return;
    }

    const loadDetail = async () => {
      setLoadingRecord(true);
      try {
        const res = await axiosInstance.get(`/code_registries/${record.id}/`);
        setFullRecord(res.data);
      } catch (e) {
        setFullRecord(record || null);
      } finally {
        setLoadingRecord(false);
      }
    };

    loadDetail();
  }, [open, record]);

  useEffect(() => {
    if (!open) return;

    setLoadingLists(true);
    setSaving(false);
    setGenerating(false);
    setSubmitted(false);

    setErrors({
      organization: "",
      department: "",
      activity: "",
      client: "",
      description: "",
      opportunities: "",
      code: "",
      submit: "",
    });

    const loadLists = async () => {
      try {
        const [orgRes, depRes, actRes, cliRes] = await Promise.all([
          axiosInstance.get("/organizations/"),
          axiosInstance.get("/departments/"),
          axiosInstance.get("/code_matrix/"),
          axiosInstance.get("/clients/"),
        ]);

        setOrganizations(orgRes.data || []);
        setDepartments(depRes.data || []);
        setActivities(actRes.data || []);
        setClients(cliRes.data || []);
      } catch (e) {
        setSubmitError(e);
      } finally {
        setLoadingLists(false);
      }
    };

    loadLists();
  }, [open]);

  const organizationOptions = useMemo(
    () =>
      (organizations || []).map((o) => ({
        value: o.id,
        label: `${o.name}${o.abbreviation ? ` (${o.abbreviation})` : ""}`,
        __raw: o,
      })),
    [organizations]
  );

  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d.id,
        label: `${d.name}${d.department_abbreviated ? ` (${d.department_abbreviated})` : ""}`,
        __raw: d,
      })),
    [departments]
  );

  const activityOptions = useMemo(() => {
    const depId = department?.value;

    const filtered = depId
      ? (activities || []).filter((m) => String(m.department) === String(depId))
      : activities || [];

    return filtered.map((m) => ({
      value: m.id,
      label: `${m.activity}${m.activity_abbreviated ? ` (${m.activity_abbreviated})` : ""}`,
      __raw: m,
    }));
  }, [activities, department]);

  const clientOptions = useMemo(
    () =>
      (clients || []).map((c) => ({
        value: c.id,
        label: c.name,
        __raw: c,
      })),
    [clients]
  );

  const opportunityOptions = useMemo(() => {
    const r = fullRecord || record;
    const opts = r?.opportunities_options || [];

    return (opts || []).map((o) => ({
      value: o.id,
      label: `${o.code_opportunity ?? `OPP#${o.id}`} - ${o.project_name ?? ""}`.trim(),
      __raw: o,
    }));
  }, [fullRecord, record]);

  useEffect(() => {
    if (!open) return;

    const r = fullRecord || record;
    if (!r) return;

    if (loadingLists || loadingRecord) return;

    const organizationPk =
      getPk(r.organization_id) ||
      getPk(r.organization) ||
      getPk(r.organization_details);

    const organizationObj =
      organizationPk != null
        ? (organizations || []).find((o) => String(o.id) === String(organizationPk)) || null
        : null;

    setOrganization(
      organizationObj
        ? {
            value: organizationObj.id,
            label: `${organizationObj.name}${organizationObj.abbreviation ? ` (${organizationObj.abbreviation})` : ""}`,
            __raw: organizationObj,
          }
        : null
    );

    const matrixPk = getPk(r.matrix_id) || getPk(r.matrix) || getPk(r.matrix_details);

    const matrixObj =
      matrixPk != null
        ? (activities || []).find((m) => String(m.id) === String(matrixPk)) || null
        : null;

    const depObj = matrixObj
      ? (departments || []).find((d) => String(d.id) === String(matrixObj.department)) || null
      : null;

    setDepartment(
      depObj
        ? {
            value: depObj.id,
            label: `${depObj.name}${depObj.department_abbreviated ? ` (${depObj.department_abbreviated})` : ""}`,
            __raw: depObj,
          }
        : null
    );

    setActivity(
      matrixObj
        ? {
            value: matrixObj.id,
            label: `${matrixObj.activity}${matrixObj.activity_abbreviated ? ` (${matrixObj.activity_abbreviated})` : ""}`,
            __raw: matrixObj,
          }
        : null
    );

    const clientPk =
      getPk(r.client_id) ||
      getPk(r.client) ||
      getPk(r.client_details) ||
      getPk(r.client_obj);

    const clientNameFromRecord =
      r.client_details?.name ||
      r.client_name ||
      r.client?.name ||
      (typeof r.client === "string" ? r.client : "") ||
      "";

    let clientObj =
      clientPk != null
        ? (clients || []).find((c) => String(c.id) === String(clientPk)) || null
        : null;

    if (!clientObj && clientNameFromRecord) {
      const needle = clientNameFromRecord.trim().toLowerCase();
      clientObj =
        (clients || []).find((c) => (c.name || "").trim().toLowerCase() === needle) || null;
    }

    setClient(
      clientObj
        ? { value: clientObj.id, label: clientObj.name, __raw: clientObj }
        : clientPk != null
          ? { value: clientPk, label: clientNameFromRecord || "Client" }
          : clientNameFromRecord
            ? { value: clientNameFromRecord, label: clientNameFromRecord }
            : null
    );

    const selectedIds = (r.opportunity_ids_selected || []).map((x) => String(x));

    const selected = (opportunityOptions || []).filter((opt) =>
      selectedIds.includes(String(opt.value))
    );

    setSelectedOpportunities(selected);

    setDescription(r.description ?? "");
    setRemarks(r.remarks ?? "");

    const fixedNr = r.nr != null ? String(r.nr) : "";
    setNr(fixedNr);
    setCode(r.code ?? "");

    initialOrganizationPkRef.current = organizationPk;
    initialMatrixPkRef.current = matrixPk;
    initialClientPkRef.current = clientPk;

    setErrors((prev) => ({
      ...prev,
      organization: "",
      department: "",
      activity: "",
      client: "",
      description: "",
      opportunities: "",
      code: "",
      submit: "",
    }));
  }, [
    open,
    record,
    fullRecord,
    loadingLists,
    loadingRecord,
    organizations,
    activities,
    departments,
    clients,
    opportunityOptions,
  ]);

  const validateAll = useCallback(() => {
    const next = {
      organization: "",
      department: "",
      activity: "",
      client: "",
      description: "",
      opportunities: "",
      code: "",
      submit: "",
    };

    if (!organization) next.organization = "Required";
    if (!department) next.department = "Required";
    if (!activity) next.activity = "Required";
    if (!client) next.client = "Required";
    if (!description.trim()) next.description = "Required";
    if (!code.trim()) next.code = "Required";

    setErrors((prev) => ({ ...prev, ...next }));

    return (
      !next.organization &&
      !next.department &&
      !next.activity &&
      !next.client &&
      !next.description &&
      !next.code
    );
  }, [organization, department, activity, client, description, code]);

  useEffect(() => {
    if (!open) return;
    if (!organization?.value || !activity?.value || !client?.value) return;

    const r = fullRecord || record;
    const fixedNr = r?.nr != null ? String(r.nr) : nr;
    if (!fixedNr) return;

    const initialOrganizationPk = initialOrganizationPkRef.current;
    const initialMatrixPk = initialMatrixPkRef.current;
    const initialClientPk = initialClientPkRef.current;

    const changed =
      (initialOrganizationPk != null &&
        String(organization.value) !== String(initialOrganizationPk)) ||
      (initialMatrixPk != null && String(activity.value) !== String(initialMatrixPk)) ||
      (initialClientPk != null && String(client.value) !== String(initialClientPk));

    if (!changed) return;

    const t = setTimeout(async () => {
      setGenerating(true);
      setErrors((prev) => ({ ...prev, submit: "" }));

      try {
        const res = await axiosInstance.post(PREVIEW_URL, {
          organization: organization.value,
          matrix: activity.value,
          client: client.value,
          nr: fixedNr,
        });

        const generatedCode = res.data?.code ?? "";
        setCode(String(generatedCode));

        if (generatedCode) {
          setErrors((prev) => ({ ...prev, code: "" }));
        }
      } catch (e) {
        setSubmitError(e);
      } finally {
        setGenerating(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [
    open,
    organization?.value,
    activity?.value,
    client?.value,
    record,
    fullRecord,
    nr,
  ]);

  const handleSubmit = async () => {
    setSubmitted(true);
    setErrors((prev) => ({ ...prev, submit: "" }));

    if (!validateAll()) return;

    if (!record?.id) {
      setErrors((prev) => ({ ...prev, submit: "Missing record.id" }));
      return;
    }

    setSaving(true);

    try {
      await axiosInstance.patch(`/code_registries/${record.id}/`, {
        organization: organization.value,
        matrix: activity.value,
        client: client.value,
        opportunity_ids: (selectedOpportunities || []).map((o) => o.value),
        description: description.trim(),
        remarks: remarks.trim(),
        code: code,
        status: (fullRecord || record)?.status ?? "Active",
      });

      onUpdated?.();
      onClose?.();
    } catch (e) {
      setSubmitError(e);
    } finally {
      setSaving(false);
    }
  };

  const disabledAll = saving || generating || loadingLists || loadingRecord;

  const showOrgError = submitted && !!errors.organization;
  const showDeptError = submitted && !!errors.department;
  const showActError = submitted && !!errors.activity;
  const showClientError = submitted && !!errors.client;
  const showDescError = submitted && !!errors.description;
  const showOppsError = submitted && !!errors.opportunities;
  const showCodeError = submitted && !!errors.code;

  return (
    <Dialog
      open={open}
      onClose={disabledAll ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="project-code-dialog"
    >
      <DialogTitle className="spc-title">
        <div className="spc-header-top">
          <span className="spc-header-title">Edit Project Code</span>
        </div>
      </DialogTitle>

      <DialogContent dividers className="cmd-content">
        {/* Organization */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Organization <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={organizationOptions}
            value={organization}
            onChange={(val) => {
              setOrganization(val);

              if (val) {
                setErrors((prev) => ({ ...prev, organization: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                organization: organization ? "" : "Required",
              }));
            }}
            isClearable
            isSearchable
            placeholder="Select organization"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showOrgError ? "cmd-select-error" : ""}
          />

          {showOrgError && (
            <div className="cmd-field-error">{errors.organization}</div>
          )}
        </div>

        {/* Department */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Department <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={departmentOptions}
            value={department}
            onChange={(val) => {
              setDepartment(val);
              setActivity(null);

              if (val) {
                setErrors((prev) => ({ ...prev, department: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                department: department ? "" : "Required",
              }));
            }}
            isClearable
            isSearchable
            placeholder="Select department"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showDeptError ? "cmd-select-error" : ""}
          />

          {showDeptError && (
            <div className="cmd-field-error">{errors.department}</div>
          )}
        </div>

        {/* Activity */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Activity <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={activityOptions}
            value={activity}
            onChange={(val) => {
              setActivity(val);

              if (val) {
                setErrors((prev) => ({ ...prev, activity: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                activity: activity ? "" : "Required",
              }));
            }}
            isClearable
            isSearchable
            placeholder={department ? "Select activity" : "First select a department"}
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll || !department}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showActError ? "cmd-select-error" : ""}
            noOptionsMessage={() =>
              !department
                ? "Please select a department first"
                : activityOptions.length === 0
                  ? "No activities available for this department"
                  : "No options"
            }
          />

          {showActError && (
            <div className="cmd-field-error">{errors.activity}</div>
          )}
        </div>

        {/* Client */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Client <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={clientOptions}
            value={client}
            onChange={(val) => {
              setClient(val);

              if (val) {
                setErrors((prev) => ({ ...prev, client: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                client: client ? "" : "Required",
              }));
            }}
            isClearable
            isSearchable
            placeholder="Select client"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showClientError ? "cmd-select-error" : ""}
          />

          {showClientError && (
            <div className="cmd-field-error">{errors.client}</div>
          )}
        </div>

        {/* Code */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Code <span style={{ color: "red" }}>*</span>
          </label>

          <input
            type="text"
            className={`cmd-input ${showCodeError ? "cmd-input-error" : ""}`}
            placeholder="Generated automatically"
            value={code}
            readOnly
            disabled
          />

          {generating && (
            <div style={{ marginTop: 6 }}>
              <CircularProgress size={18} />
            </div>
          )}

          {showCodeError && (
            <div className="cmd-field-error">{errors.code}</div>
          )}
        </div>

        {/* Description */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Description <span style={{ color: "red" }}>*</span>
          </label>

          <textarea
            rows={3}
            className={`cmd-input ${showDescError ? "cmd-input-error" : ""}`}
            placeholder="Enter description..."
            value={description}
            onChange={(e) => {
              const val = e.target.value;
              setDescription(val);

              if (val.trim()) {
                setErrors((prev) => ({ ...prev, description: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                description: description.trim() ? "" : "Required",
              }));
            }}
            disabled={disabledAll}
            style={{ resize: "vertical" }}
          />

          {showDescError && (
            <div className="cmd-field-error">{errors.description}</div>
          )}
        </div>

        {/* Opportunities */}
        <div className="cmd-form-group">
          <label className="cmd-label">Opportunities</label>

          <Select
            options={opportunityOptions}
            value={selectedOpportunities}
            onChange={(vals) => {
              setSelectedOpportunities(vals || []);

              if (vals && vals.length > 0) {
                setErrors((prev) => ({ ...prev, opportunities: "" }));
              }
            }}
            isMulti
            isClearable
            isSearchable
            placeholder="Select opportunities"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showOppsError ? "cmd-select-error" : ""}
            noOptionsMessage={() => "No opportunities available"}
          />

          {showOppsError && (
            <div className="cmd-field-error">{errors.opportunities}</div>
          )}
        </div>

        {/* Status */}
        <div className="cmd-form-group">
          <label className="cmd-label">Status</label>

          <input
            type="text"
            className="cmd-input"
            value={(fullRecord || record)?.status ?? "Active"}
            disabled
            readOnly
          />
        </div>

        {errors.submit && (
          <div className="cmd-submit-error">{errors.submit}</div>
        )}
      </DialogContent>

      <DialogActions className="cmd-actions">
        <Button
          onClick={onClose}
          disabled={disabledAll}
          className="cmd-btn cmd-btn-cancel"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={disabledAll}
          variant="contained"
          className="cmd-btn cmd-btn-create"
          startIcon={
            saving ? <CircularProgress size={18} className="cmd-spinner" /> : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}